// What the loader remembers per bot, in the host page's own storage (first-party, so it survives third-party
// storage partitioning): the visitor token, the conversation, the character, whether it was open, unread count,
// teaser state.

export interface Saved {
  v: 1;
  visitorToken: string | null;
  conversationId: string | null;
  resumeToken: string | null;
  /** the character the visitor switched to (null: the bot itself) */
  character: string | null;
  open: boolean;
  unread: number;
  teaserDismissed: boolean;
  lastActiveAt: number;
}

const empty = (): Saved => ({
  v: 1,
  visitorToken: null,
  conversationId: null,
  resumeToken: null,
  character: null,
  open: false,
  unread: 0,
  teaserDismissed: false,
  lastActiveAt: 0,
});

export class Store {
  private memory: Saved = empty();

  constructor(
    private readonly key: string,
    private mode: 'local' | 'session' | 'none',
  ) {
    this.memory = this.read();
  }

  private area(): Storage | null {
    try {
      return this.mode === 'local' ? window.localStorage : this.mode === 'session' ? window.sessionStorage : null;
    } catch {
      return null;
    }
  }

  private read(): Saved {
    try {
      const raw = this.area()?.getItem(this.key);
      const data = raw ? (JSON.parse(raw) as Saved) : null;
      return data?.v === 1 ? { ...empty(), ...data } : empty();
    } catch {
      return empty();
    }
  }

  get(): Saved {
    return this.memory;
  }

  set(patch: Partial<Saved>) {
    this.memory = { ...this.memory, ...patch, lastActiveAt: Date.now() };
    try {
      this.area()?.setItem(this.key, JSON.stringify(this.memory));
    } catch {
      // storage full or blocked: keep it in memory
    }
  }

  /** Move what's remembered to another place; 'none' (no consent) forgets it. */
  setMode(mode: 'local' | 'session' | 'none') {
    if (mode === this.mode) return;
    const keep = this.memory;
    this.forget();
    this.mode = mode;
    if (mode !== 'none') this.set(keep);
  }

  forget() {
    try {
      this.area()?.removeItem(this.key);
    } catch {
      // nothing to remove
    }
    this.memory = empty();
  }
}
